import * as Sentry from "@sentry/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { faInfoCircle, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AdminContainer, AdminTitle } from "../../../components/Admin";
import Button from "../../../components/Button";
import { Grid, GridCol } from "../../../components/Grid";
import Toggle from "../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import ConfirmationModal from "../../../components/Modal/Templates/ConfirmationModal";
import OptionsDrawer from "../../../components/OptionsDrawer";
import Spacer from "../../../components/Spacer";
import Spinner from "../../../components/Spinner/ComponentLoader";
import Table from "../../../components/TableLite";
import Text from "../../../components/Text";
import TruncatedText from "../../../components/Text/TruncatedText";

import { usePageMeta } from "../../../../public/Hooks/usePageMeta";
import { useCompanyFunction, useGetScimTokens, useRbacPrimaryRolePermissions } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import useToast from "../../../hooks/useToast";

import { type ScimToken, createScimToken, deleteScimToken, updateScimToken } from "../../../api/services";

import css from "./SCIMSettings.module.scss";

interface AddSCIMTokenButtonProps {
	disabled: boolean;
}

const AddSCIMTokenButton = (props: AddSCIMTokenButtonProps) => {
	const { disabled } = props;

	const navigate = useNavigate();

	const mutation = useMutation({
		mutationFn: createScimToken,
		onSuccess: (resp) => {
			navigate(`/administration/scim/${resp.data._id}`);
		},
	});

	return (
		<div data-testid="addSCIMTokenButton">
			<Button
				onClick={() => {
					const titleDate = format(new Date(), "MM/dd/yyyy");
					mutation.mutate({
						// Setting Default Name
						name: `SCIM ${titleDate}`,
						enabled: true,
						manager_sync_enabled: false,
					});
				}}
				disabled={disabled}
				icon={faPlus}
				isRequesting={mutation.isPending}
			>
				Add SCIM Integration
			</Button>
		</div>
	);
};

const SCIMSettings = () => {
	const [deleteModal, setDeleteModal] = useState<null | { id: string; name: string }>(null);
	const [currentSort, setCurrentSort] = useState<{ column: string; direction: "asc" | "desc" }>({
		column: "name",
		direction: "asc",
	});

	const { value: scimFeatureVisible } = useFeatureFlagQuery("ff-is-scim-enabled", false);
	const { companyFunction: companyScimFunction, isLoading } = useCompanyFunction("scim_api");
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("scim_api");
	const toast = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	usePageMeta({
		title: "SCIM Settings",
		description: "View SCIM Integration",
	});

	const {
		data: scimTokensResponse,
		isLoading: isQueryLoading,
		refetch,
		isFetching,
	} = useGetScimTokens(currentSort.column, currentSort.direction);

	const isAddScimIntegrationButtonDisabled = !!scimTokensResponse?.data?.length;

	const handleDelete = async () => {
		if (!deleteModal) return;
		await deleteScimToken(deleteModal.id);
		setDeleteModal(null);
		refetch();
		toast("SCIM Integration deleted", { type: "success" });
	};

	const handleCancelModal = () => {
		setDeleteModal(null);
	};

	const handleSortColumn = (column: string, direction: "asc" | "desc") => {
		setCurrentSort({ column, direction });
	};

	const modal = deleteModal ? (
		<ConfirmationModal
			positiveAction={handleDelete}
			negativeAction={handleCancelModal}
			positiveActionLabel="Yes"
			negativeActionLabel="Cancel"
		>
			<>
				Are you sure you want to delete &quot;
				<Text element="span" size="paragraph-2" weight="semi-bold">
					{deleteModal.name}
				</Text>
				&quot;?
			</>
		</ConfirmationModal>
	) : null;

	const tableColumns = [
		{
			id: "name",
			label: "Name",
			sortName: "name",
			width: "275px",
			getter: (row) => {
				return <TruncatedText maxWidth={275} size="paragraph-3" weight="regular" text={row.name} />;
			},
		},
		{
			id: "last_used_at",
			label: "Last Used",
			sortName: "last_used_at",
			width: "4fr",
			getter: (row) => {
				if (!row?.last_used_at) {
					return null;
				}

				return format(row.last_used_at, "MMM d, yyyy h:mm a");
			},
		},
		{
			id: "enabled",
			label: "Enabled",
			sortName: "enabled",
			width: "4fr",
			getter: (row: ScimToken) => {
				const editMutation = useMutation({
					mutationFn: updateScimToken,
					onMutate: async (updatedSCIMToken) => {
						row.enabled = updatedSCIMToken.enabled;

						// Cancel any outgoing refetch (so they don't overwrite our optimistic update)
						await queryClient.cancelQueries({ queryKey: ["getScimTokens", updatedSCIMToken._id] });

						// Snapshot the previous value
						const previousValue = queryClient.getQueryData(["getScimTokens", updatedSCIMToken._id]);

						// Optimistically update to the new value
						queryClient.setQueryData(["getScimTokens", updatedSCIMToken._id], updatedSCIMToken);

						// Return a context object with the snapshot value
						return { previousValue, updatedSCIMToken };
					},
					onSettled: (updatedSCIMToken) => {
						queryClient.invalidateQueries({ queryKey: ["getScimTokens", updatedSCIMToken?._id] });
					},
					onSuccess: () => {
						toast(`SCIM Integration ${row.enabled ? "Enabled" : "Disabled"}`, { type: "success" });
						refetch();
					},
					onError: (_err, _updatedSCIMToken, context) => {
						queryClient.setQueryData(
							["getScimTokens", context?.updatedSCIMToken?._id],
							context?.previousValue
						);

						toast(`Unable to ${!row.enabled ? "Enable" : "Disable"} SCIM Integration`, { type: "error" });
						refetch();
					},
				});

				return (
					<div className={css.buttonContainer}>
						<Toggle
							onChange={() => {
								editMutation.mutate({
									_id: row._id,
									name: row.name,
									enabled: !row.enabled,
									manager_sync_enabled: row.manager_sync_enabled,
								});
							}}
							value={row.enabled}
						/>
					</div>
				);
			},
		},
		{
			id: "controls",
			label: "",
			isSortable: false,
			width: "4fr",
			getter: (row) => {
				const handleOptionsDrawer = (option: { id: string; label: string }) => {
					switch (option.id) {
						case "view-sync-log":
							navigate(`/administration/scim/${row._id}/sync-log`);
							break;
						case "delete-token":
							setDeleteModal({ id: row._id, name: row.name });
							break;
						case "edit-token":
							navigate(`/administration/scim/${row._id}`);
							break;
						default:
							break;
					}
				};

				// Render Ellipsis Action Menu
				return (
					<div data-testid="SCIMTokenOptionDrawer" className={css.optionsDrawer}>
						<OptionsDrawer
							additionalClasses={css.optionsDrawerButton}
							horizontal={true}
							options={[
								{ id: "view-sync-log", label: "View Sync Log" },
								{ id: "edit-token", label: "Edit" },
								{ id: "delete-token", label: "Delete" },
							]}
							onSelect={handleOptionsDrawer}
						/>
					</div>
				);
			},
		},
	];

	if (isQueryLoading || isRbacLoading || isLoading || isFetching) {
		return <Spinner size={28} />;
	}

	return (
		<>
			<Sentry.ErrorBoundary fallback={undefined}>
				<AdminContainer>
					{companyScimFunction !== "enabled" || !scimFeatureVisible ? (
						<>
							{isLoading ? null : (
								<div className={css.infoWrapper}>
									<AdminTitle includeSpacer={false}>SCIM</AdminTitle>

									<div className={css.infoBox}>
										<div className={css.infoIcon}>
											<FontAwesomeIcon icon={faInfoCircle} />
										</div>
										<div>This feature is not enabled for your organization.</div>
									</div>
								</div>
							)}
						</>
					) : (
						<>
							{isRbacLoading ? null : (
								<>
									{!rbacPermissions?.includes("scim_api:viewScimApiToken") ? (
										<>
											<div className={css.infoWrapper}>
												<AdminTitle includeSpacer={false}>SCIM</AdminTitle>

												<div className={css.infoBox}>
													<div className={css.infoIcon}>
														<FontAwesomeIcon icon={faInfoCircle} />
													</div>
													<div>Missing required permissions.</div>
												</div>
											</div>
										</>
									) : (
										<>
											{modal}
											<AdminTitle includeSpacer={false}>SCIM</AdminTitle>

											<section className={css.scimContainer}>
												<div data-testid="addSCIMTokenButton">
													<AddSCIMTokenButton disabled={isAddScimIntegrationButtonDisabled} />
												</div>
												<Spacer height={2} />
												<div data-testid="SCIMTokenTable" className={css.table}>
													<Table
														isLoading={isQueryLoading}
														columns={tableColumns}
														rows={scimTokensResponse?.data || []}
														rowKey="_id"
														onColumnClick={handleSortColumn}
														activeSort={currentSort.column}
														activeSortDirection={currentSort.direction}
														theme={"vJan23"}
														emptyText={
															<>
																<Spacer height={9} />
																<Grid cols={3}>
																	<GridCol width="1fr" />
																	<GridCol
																		className={css.noResultsContent}
																		width={350}
																	>
																		<div>
																			<Text
																				element="h3"
																				size="paragraph-2"
																				weight="semi-bold"
																			>
																				You currently have no SCIM Integrations.
																				Add a SCIM Integration to get started.
																			</Text>
																		</div>
																		<Spacer height={2} />
																		<div className={css.noResultsContentButton}>
																			<AddSCIMTokenButton
																				disabled={
																					isAddScimIntegrationButtonDisabled
																				}
																			/>
																		</div>
																	</GridCol>
																	<GridCol width="1fr" />
																</Grid>
																<Spacer height={9} />
															</>
														}
													/>
												</div>
											</section>
										</>
									)}
								</>
							)}
						</>
					)}
				</AdminContainer>
			</Sentry.ErrorBoundary>
		</>
	);
};

export default SCIMSettings;
